import { LitElement, css, html, nothing } from "lit";
import type { PropertyValues, TemplateResult } from "lit";
import type { RuleEvent, RuleOutcome } from "@newsfeed/shared";
import type { ApiClient } from "../api.js";
import { ApiError } from "../api.js";
import { formatDate } from "../format.js";
import { navigate } from "../router.js";
import { sharedStyles } from "../styles.js";

/** Observability: the queryable rule-event stream for one rule. */
export class NfRuleActivityView extends LitElement {
  static override properties = {
    api: { attribute: false },
    ruleId: { attribute: false },
    events: { state: true },
    outcome: { state: true },
    since: { state: true },
    until: { state: true },
    loading: { state: true },
    error: { state: true },
  };

  api!: ApiClient;
  ruleId = "";
  events: RuleEvent[] = [];
  outcome: RuleOutcome | "" = "";
  since = "";
  until = "";
  loading = false;
  error = "";

  static override styles = [
    sharedStyles,
    css`
      header {
        display: flex;
        align-items: center;
        gap: var(--nf-space-12);
        margin-bottom: var(--nf-space-12);
      }
      h2 {
        flex: 1;
        font-size: var(--nf-font-18);
      }
      .filters {
        display: flex;
        gap: var(--nf-space-12);
        flex-wrap: wrap;
        align-items: flex-end;
        margin-bottom: var(--nf-space-16);
      }
      .filter {
        display: flex;
        flex-direction: column;
        gap: var(--nf-space-4);
        font-size: var(--nf-font-13);
      }
      .event {
        padding: var(--nf-space-12);
        border-bottom: 1px solid var(--nf-border);
      }
      .event:last-child {
        border-bottom: none;
      }
      .event-head {
        display: flex;
        gap: var(--nf-space-8);
        align-items: center;
      }
      .badge.failed {
        background: var(--nf-danger);
        color: var(--nf-accent-fg);
      }
      .badge.succeeded {
        background: var(--nf-success);
        color: var(--nf-accent-fg);
      }
      .detail {
        font-size: var(--nf-font-13);
        margin-top: var(--nf-space-4);
      }
    `,
  ];

  override updated(changed: PropertyValues<this>): void {
    if (changed.has("api") || changed.has("ruleId")) void this.load();
  }

  private async load(): Promise<void> {
    if (!this.api || this.ruleId === "") return;
    this.loading = true;
    this.error = "";
    try {
      this.events = await this.api.listRuleEvents({
        rule: this.ruleId,
        outcome: this.outcome === "" ? undefined : this.outcome,
        since: this.since === "" ? undefined : new Date(this.since).getTime(),
        until: this.until === "" ? undefined : new Date(this.until).getTime(),
        limit: 200,
      });
    } catch (err) {
      this.error = err instanceof ApiError ? err.message : String(err);
    } finally {
      this.loading = false;
    }
  }

  private get ruleName(): string {
    return this.events[0]?.ruleName ?? "Rule";
  }

  override render(): TemplateResult {
    return html`
      <header>
        <button @click=${() => navigate("/rules")}>← Rules</button>
        <h2>${this.ruleName} · activity</h2>
      </header>

      <div class="filters">
        <div class="filter">
          <label>Outcome</label>
          <select
            @change=${(e: Event) => {
              if (e.target instanceof HTMLSelectElement) {
                this.outcome =
                  e.target.value === "succeeded" || e.target.value === "failed"
                    ? e.target.value
                    : "";
                void this.load();
              }
            }}
          >
            <option value="" ?selected=${this.outcome === ""}>All</option>
            <option value="succeeded" ?selected=${this.outcome === "succeeded"}>
              Succeeded
            </option>
            <option value="failed" ?selected=${this.outcome === "failed"}>
              Failed
            </option>
          </select>
        </div>
        <div class="filter">
          <label>Since</label>
          <input
            type="datetime-local"
            .value=${this.since}
            @change=${(e: Event) => {
              if (e.target instanceof HTMLInputElement) {
                this.since = e.target.value;
                void this.load();
              }
            }}
          />
        </div>
        <div class="filter">
          <label>Until</label>
          <input
            type="datetime-local"
            .value=${this.until}
            @change=${(e: Event) => {
              if (e.target instanceof HTMLInputElement) {
                this.until = e.target.value;
                void this.load();
              }
            }}
          />
        </div>
      </div>

      ${this.error ? html`<div class="card error">${this.error}</div>` : nothing}
      ${
        this.loading
          ? html`<div class="empty">Loading…</div>`
          : this.events.length === 0
            ? html`<div class="empty">No events match.</div>`
            : html`<div class="card" style="padding:0">
                ${this.events.map(
                  (ev) =>
                    html`<div class="event">
                    <div class="event-head">
                      <span class="badge ${ev.outcome}">${ev.outcome}</span>
                      <span class="muted">${formatDate(ev.occurredAt)}</span>
                      ${ev.matchedConditions ? html`<span class="muted">matched</span>` : nothing}
                    </div>
                    <div class="detail">
                      ${ev.feedUrl ? html`<span class="muted">${ev.feedUrl}</span> · ` : nothing}
                      actions:
                      ${ev.actionsTaken.map((a) => a.action).join(", ") || "none"}
                      ${ev.detail ? html`<div class="muted">${ev.detail}</div>` : nothing}
                    </div>
                  </div>`,
                )}
              </div>`
      }
    `;
  }
}

customElements.define("nf-rule-activity-view", NfRuleActivityView);

declare global {
  interface HTMLElementTagNameMap {
    "nf-rule-activity-view": NfRuleActivityView;
  }
}
